import React, { createContext, useContext, useEffect, useState } from "react";

const DICT = {
  en: {
    appName: "StitchQuote",
    tagline: "Digitize. Cost. Quote — in one flow.",
    langLabel: "Language",
    // Auth
    phonePlaceholder: "Phone number",
    phoneHelp: "We'll send a 6-digit code",
    sendOtp: "Send OTP",
    enterOtp: "Enter OTP",
    otpHint: "Use 123456 for now",
    yourName: "Your name (optional)",
    businessName: "Business name (optional)",
    verify: "Verify & Continue",
    changePhone: "Change number",
    welcomeBack: "Welcome back — enter OTP to continue.",
    completeProfile: "First time here? Add your details once (optional).",
    // Nav
    home: "Home",
    orders: "Orders",
    settings: "Settings",
    logout: "Log out",
    // Home
    welcome: "Welcome",
    newDesign: "New design",
    newDesignSub: "Upload artwork → get a quote",
    quickStats: "This month",
    totalDesigns: "Total designs",
    monthDesigns: "This month",
    totalValue: "Quoted value",
    quotesShared: "Shared",
    recent: "Recent orders",
    empty: "No designs yet. Tap New design to start.",
    // Upload / form
    uploadTitle: "Upload design",
    tapCamera: "Take photo",
    tapGallery: "From gallery",
    changePhoto: "Change photo",
    details: "Design details",
    designName: "Design name",
    garmentType: "Garment type",
    machineBrand: "Machine brand",
    fabricType: "Fabric type",
    stitches: "Stitch count",
    machineTime: "Machine time (min)",
    threadColors: "Thread colors",
    outputFormat: "Output format",
    generate: "Generate quote",
    aiAnalyze: "Analyze design (AI)",
    aiEstimate: "AI estimate",
    aiFilled: "AI filled the stitch details — you can still edit.",
    aiCoverage: "Coverage",
    aiSize: "Size",
    aiFillType: "Fill type",
    aiWork: "Work",
    aiMirror: "Mirror",
    aiNotes: "Notes",
    aiTip: "Recommendation",
    approxPricePerPiece: "Approx price / piece",
    // Processing
    procAnalyzing: "Analyzing design",
    procDetecting: "Detecting motifs",
    procCalculating: "Calculating stitches",
    procGenerating: "Generating quote",
    // Results
    resultsTitle: "Results",
    stitchDetails: "Stitch details",
    priceQuote: "Price quote",
    stitchCount: "Stitch count",
    estMachineTime: "Est. machine time",
    minutes: "min",
    colors: "colors",
    format: "Format",
    downloadFile: "Download stitch file",
    materialCost: "Material cost",
    machineCost: "Machine cost",
    laborCost: "Labor cost",
    suggestedPrice: "Suggested price",
    finalPrice: "Final price (₹)",
    savePrice: "Save price",
    delivery: "Est. delivery",
    shareQuote: "Share quote",
    shareCopied: "Client quote link copied!",
    downloadPdf: "Download PDF",
    shareWhatsapp: "Share on WhatsApp",
    waMessage: "Hello! Here is your quote from {business}: ₹{price}, delivery by {date}. View: {url}",
    // Orders
    ordersTitle: "Order history",
    search: "Search designs",
    // Settings
    settingsTitle: "Cost settings",
    meterPricing: "Meter-wise stitch pricing",
    meterPricingHint: "Formula: (stitches ÷ 1000) × heads/meter × paisa rate. 1000mm = 1 meter.",
    stitchRatePaisa: "Stitch rate (paisa per 1000 stitches)",
    headsPerMeter: "Heads per meter",
    headsPerMeterHint: "{mm}mm frame → {hpm} heads occupy 1 meter (1000mm)",
    stitchMeterCost: "Stitch work (meter-wise)",
    meterFormulaTitle: "Meter-wise calculation",
    pricePerMeter: "Rate per meter (1 head column)",
    machineRate: "Machine hour rate (₹)",
    laborRate: "Labor hour rate (₹)",
    margin: "Margin %",
    threadPrice: "Thread price (₹/g)",
    zariPrice: "Zari price (₹/g)",
    sequinPrice: "Sequin price (₹/unit)",
    saveSettings: "Save settings",
    saved: "Saved",
    // Share public
    clientQuote: "Client quote",
    priceLabel: "Price",
    deliveryBy: "Delivery by",
    from: "From",
    // Common
    back: "Back",
    // Machine area
    machineArea: "Machine area (heads × frame)",
    defaultMachineArea: "Default machine area",
    defaultMachineAreaHint: "This will be pre-selected each time you create a new design",
    machineAreaHint: "Running {n} pieces simultaneously — machine & labor cost is divided by {n} per piece",
    machineAreaResultNote: "machine & labor cost shared across {n} pieces",
    heads: "heads",
    // Garment options
    blouse: "Blouse",
    kurti: "Kurti",
    sareeBorder: "Saree border",
    lehenga: "Lehenga",
    dupatta: "Dupatta",
    salwar: "Salwar",
    anarkali: "Anarkali",
    gown: "Gown",
    odhni: "Odhni / Chunri",
    ghagra: "Ghagra / Skirt",
    jacket: "Jacket / Shrug",
    waistcoat: "Waistcoat / Koti",
    sareePallu: "Saree pallu",
    kurta: "Kurta (gents)",
    // Embellishments (bits)
    embellishments: "Embellishments (Bits)",
    embellishmentsHint: "Toggle each material; enter approx quantity & weight used per piece",
    zari: "Zari work",
    sequin: "Sequin / Tikki",
    moti: "Moti (Pearl beads)",
    mirror: "Mirror / Shisha / Abla",
    kundan: "Kundan stones",
    stone: "Rhinestone / Crystal",
    khatli: "Khatli (metallic foil)",
    gota: "Gota Patti (ribbon)",
    pipe_bead: "Pipe / Bugle beads",
    nakh: "Nakh (nail bits)",
    approxCount: "Approx qty (pcs)",
    approxGrams: "Approx weight (g)",
    aiBitsDetected: "AI detected bits / materials",
    bitsBreakdown: "Bits / embellishment details",
    yes: "Yes",
    no: "No",
  },
  hi: {
    appName: "StitchQuote",
    tagline: "डिज़ाइन → लागत → क्विक क्वोट — एक ही जगह।",
    langLabel: "भाषा",
    phonePlaceholder: "फ़ोन नंबर",
    phoneHelp: "हम 6-अंकों का OTP भेजेंगे",
    sendOtp: "OTP भेजें",
    enterOtp: "OTP दर्ज करें",
    otpHint: "अभी 123456 डालें",
    yourName: "आपका नाम (वैकल्पिक)",
    businessName: "व्यवसाय का नाम (वैकल्पिक)",
    verify: "जांचें और आगे बढ़ें",
    changePhone: "नंबर बदलें",
    welcomeBack: "वापसी पर स्वागत है — OTP दर्ज करें और आगे बढ़ें।",
    completeProfile: "पहली बार? अपना विवरण एक बार भरें (वैकल्पिक)।",
    home: "होम",
    orders: "ऑर्डर",
    settings: "सेटिंग्स",
    logout: "लॉग आउट",
    welcome: "स्वागत है",
    newDesign: "नया डिज़ाइन",
    newDesignSub: "फ़ोटो अपलोड करें → क्वोट पाएं",
    quickStats: "इस महीने",
    totalDesigns: "कुल डिज़ाइन",
    monthDesigns: "इस महीने",
    totalValue: "कुल मूल्य",
    quotesShared: "साझा किए",
    recent: "हाल के ऑर्डर",
    empty: "अभी कोई डिज़ाइन नहीं। शुरू करने के लिए 'नया डिज़ाइन' दबाएँ।",
    uploadTitle: "डिज़ाइन अपलोड",
    tapCamera: "फ़ोटो लें",
    tapGallery: "गैलरी से",
    changePhoto: "फ़ोटो बदलें",
    details: "डिज़ाइन विवरण",
    designName: "डिज़ाइन का नाम",
    garmentType: "परिधान का प्रकार",
    machineBrand: "मशीन ब्रांड",
    fabricType: "कपड़े का प्रकार",
    stitches: "स्टिच काउंट",
    machineTime: "मशीन समय (मिनट)",
    threadColors: "धागे के रंग",
    outputFormat: "आउटपुट फ़ॉर्मेट",
    generate: "क्वोट बनाएँ",
    aiAnalyze: "डिज़ाइन विश्लेषण (AI)",
    aiEstimate: "AI अनुमान",
    aiFilled: "AI ने विवरण भर दिया — आप संपादित कर सकते हैं।",
    aiCoverage: "क्षेत्रफल",
    aiSize: "आकार",
    aiFillType: "फ़िल प्रकार",
    aiWork: "काम",
    aiMirror: "शीशा",
    aiNotes: "नोट्स",
    aiTip: "सुझाव",
    approxPricePerPiece: "प्रति पीस अनुमानित मूल्य",
    procAnalyzing: "डिज़ाइन विश्लेषण",
    procDetecting: "मोटिफ़ पहचान",
    procCalculating: "स्टिच गणना",
    procGenerating: "क्वोट तैयार",
    resultsTitle: "परिणाम",
    stitchDetails: "स्टिच विवरण",
    priceQuote: "मूल्य क्वोट",
    stitchCount: "स्टिच काउंट",
    estMachineTime: "अनुमानित मशीन समय",
    minutes: "मिनट",
    colors: "रंग",
    format: "फ़ॉर्मेट",
    downloadFile: "स्टिच फ़ाइल डाउनलोड",
    materialCost: "सामग्री लागत",
    machineCost: "मशीन लागत",
    laborCost: "मज़दूरी",
    suggestedPrice: "सुझावित मूल्य",
    finalPrice: "अंतिम मूल्य (₹)",
    savePrice: "मूल्य सहेजें",
    delivery: "डिलीवरी अनुमान",
    shareQuote: "क्वोट साझा करें",
    shareCopied: "क्लाइंट क्वोट लिंक कॉपी हुआ!",
    downloadPdf: "PDF डाउनलोड",
    shareWhatsapp: "WhatsApp पर भेजें",
    waMessage: "नमस्ते! {business} से आपका क्वोट: ₹{price}, डिलीवरी {date}. देखें: {url}",
    ordersTitle: "ऑर्डर इतिहास",
    search: "डिज़ाइन खोजें",
    settingsTitle: "लागत सेटिंग्स",
    meterPricing: "मीटर अनुसार स्टिच मूल्य",
    meterPricingHint: "सूत्र: (स्टिच ÷ 1000) × हेड/मीटर × पैसा दर। 1000mm = 1 मीटर।",
    stitchRatePaisa: "स्टिच दर (प्रति 1000 स्टिच पैसा)",
    headsPerMeter: "प्रति मीटर हेड",
    headsPerMeterHint: "{mm}mm फ्रेम → 1 मीटर में {hpm} हेड",
    stitchMeterCost: "स्टिच काम (मीटर अनुसार)",
    meterFormulaTitle: "मीटर अनुसार गणना",
    pricePerMeter: "प्रति मीटर दर (1 हेड कॉलम)",
    machineRate: "मशीन प्रति घंटा (₹)",
    laborRate: "मज़दूरी प्रति घंटा (₹)",
    margin: "मार्जिन %",
    threadPrice: "धागा (₹/g)",
    zariPrice: "ज़री (₹/g)",
    sequinPrice: "सीक्वेन (₹/pc)",
    saveSettings: "सेटिंग्स सहेजें",
    saved: "सहेजा गया",
    clientQuote: "क्लाइंट क्वोट",
    priceLabel: "मूल्य",
    deliveryBy: "डिलीवरी तक",
    from: "प्रेषक",
    back: "वापस",
    machineArea: "मशीन क्षेत्र (हेड × फ्रेम)",
    defaultMachineArea: "डिफ़ॉल्ट मशीन क्षेत्र",
    defaultMachineAreaHint: "नया डिज़ाइन बनाते समय यह पहले से चुना जाएगा",
    machineAreaHint: "{n} टुकड़े एक साथ चल रहे हैं — मशीन और मजदूरी लागत {n} से विभाजित होती है",
    machineAreaResultNote: "मशीन और मजदूरी लागत {n} टुकड़ों में साझा",
    heads: "हेड",
    blouse: "ब्लाउज़",
    kurti: "कुर्ती",
    sareeBorder: "साड़ी बॉर्डर",
    lehenga: "लहंगा",
    dupatta: "दुपट्टा",
    salwar: "सलवार",
    anarkali: "अनारकली",
    gown: "गाउन",
    odhni: "ओढ़नी / चुनरी",
    ghagra: "घाघरा / स्कर्ट",
    jacket: "जैकेट / श्रग",
    waistcoat: "वेस्टकोट / कोटी",
    sareePallu: "साड़ी पल्लू",
    kurta: "कुर्ता (पुरुष)",
    embellishments: "कढ़ाई सामग्री (बिट्स)",
    embellishmentsHint: "प्रत्येक सामग्री चालू करें; प्रति पीस अनुमानित मात्रा और वजन दर्ज करें",
    zari: "ज़री काम",
    sequin: "सीक्वेन / टिकी",
    moti: "मोती (बीड्स)",
    mirror: "शीशा / अब्ला",
    kundan: "कुंदन स्टोन",
    stone: "राइनस्टोन / क्रिस्टल",
    khatli: "खटली (मेटलिक फोइल)",
    gota: "गोटा पट्टी",
    pipe_bead: "पाइप / बुगल बीड्स",
    nakh: "नख (नेल बिट्स)",
    approxCount: "अनुमानित संख्या (pcs)",
    approxGrams: "अनुमानित वजन (g)",
    aiBitsDetected: "AI द्वारा पहचानी गई सामग्री",
    bitsBreakdown: "बिट्स / सामग्री विवरण",
    yes: "हाँ",
    no: "नहीं",
  },
  gu: {
    appName: "StitchQuote",
    tagline: "ડિઝાઇન → ખર્ચ → ક્વોટ — એક જ ફ્લોમાં.",
    langLabel: "ભાષા",
    phonePlaceholder: "ફોન નંબર",
    phoneHelp: "અમે 6-આંકનો OTP મોકલશું",
    sendOtp: "OTP મોકલો",
    enterOtp: "OTP દાખલ કરો",
    otpHint: "હાલ પુરતું 123456 વાપરો",
    yourName: "તમારું નામ (વૈકલ્પિક)",
    businessName: "વ્યવસાયનું નામ (વૈકલ્પિક)",
    verify: "ચકાસો અને આગળ વધો",
    changePhone: "નંબર બદલો",
    welcomeBack: "પાછા આવ્યા — OTP દાખલ કરો અને આગળ વધો.",
    completeProfile: "પહેલી વાર? તમારી વિગતો એક વાર ભરો (વૈકલ્પિક).",
    home: "હોમ",
    orders: "ઓર્ડર",
    settings: "સેટિંગ્સ",
    logout: "લોગ આઉટ",
    welcome: "સ્વાગત છે",
    newDesign: "નવી ડિઝાઇન",
    newDesignSub: "આર્ટવર્ક અપલોડ કરો → ક્વોટ મેળવો",
    quickStats: "આ મહિનો",
    totalDesigns: "કુલ ડિઝાઇન",
    monthDesigns: "આ મહિનો",
    totalValue: "ક્વોટ મૂલ્ય",
    quotesShared: "શેર કરેલા",
    recent: "તાજેતરના ઓર્ડર",
    empty: "હજી કોઈ ડિઝાઇન નથી. શરૂ કરવા 'નવી ડિઝાઇન' દબાવો.",
    uploadTitle: "ડિઝાઇન અપલોડ",
    tapCamera: "ફોટો લો",
    tapGallery: "ગેલેરીથી",
    changePhoto: "ફોટો બદલો",
    details: "ડિઝાઇન વિગતો",
    designName: "ડિઝાઇનનું નામ",
    garmentType: "કપડાનો પ્રકાર",
    machineBrand: "મશીન બ્રાન્ડ",
    fabricType: "ફેબ્રિકનો પ્રકાર",
    stitches: "સ્ટીચ કાઉન્ટ",
    machineTime: "મશીન સમય (મિનિટ)",
    threadColors: "થ્રેડ કલર્સ",
    outputFormat: "આઉટપુટ ફોર્મેટ",
    generate: "ક્વોટ બનાવો",
    aiAnalyze: "ડિઝાઇન વિશ્લેષણ (AI)",
    aiEstimate: "AI અંદાજ",
    aiFilled: "AI એ વિગતો ભરી દીધી — તમે ફેરફાર કરી શકો છો.",
    aiCoverage: "કવરેજ",
    aiSize: "કદ",
    aiFillType: "ફિલ પ્રકાર",
    aiWork: "કામ",
    aiMirror: "શીશા",
    aiNotes: "નોંધ",
    aiTip: "ભલામણ",
    approxPricePerPiece: "પ્રતિ પીસ અંદાજિત ભાવ",
    procAnalyzing: "ડિઝાઇન વિશ્લેષણ",
    procDetecting: "મોટિફ ઓળખ",
    procCalculating: "સ્ટીચ ગણતરી",
    procGenerating: "ક્વોટ તૈયાર",
    resultsTitle: "પરિણામ",
    stitchDetails: "સ્ટીચ વિગતો",
    priceQuote: "પ્રાઇસ ક્વોટ",
    stitchCount: "સ્ટીચ કાઉન્ટ",
    estMachineTime: "અંદાજિત મશીન સમય",
    minutes: "મિનિટ",
    colors: "કલર્સ",
    format: "ફોર્મેટ",
    downloadFile: "સ્ટીચ ફાઇલ ડાઉનલોડ",
    materialCost: "મટીરીયલ ખર્ચ",
    machineCost: "મશીન ખર્ચ",
    laborCost: "મજૂરી",
    suggestedPrice: "સૂચિત ભાવ",
    finalPrice: "અંતિમ ભાવ (₹)",
    savePrice: "ભાવ સાચવો",
    delivery: "ડિલિવરી અંદાજ",
    shareQuote: "ક્વોટ શેર કરો",
    shareCopied: "ક્લાયન્ટ ક્વોટ લિંક કોપી થઈ!",
    downloadPdf: "PDF ડાઉનલોડ",
    shareWhatsapp: "WhatsApp પર મોકલો",
    waMessage: "નમસ્તે! {business} તરફથી તમારો ક્વોટ: ₹{price}, ડિલિવરી {date}. જુઓ: {url}",
    ordersTitle: "ઓર્ડર ઇતિહાસ",
    search: "ડિઝાઇન શોધો",
    settingsTitle: "કોસ્ટ સેટિંગ્સ",
    meterPricing: "મીટર પ્રમાણે સ્ટીચ ભાવ",
    meterPricingHint: "ફોર્મ્યુલા: (સ્ટીચ ÷ 1000) × હેડ/મીટર × પૈસા દર. 1000mm = 1 મીટર.",
    stitchRatePaisa: "સ્ટીચ દર (પ્રતિ 1000 સ્ટીચ પૈસા)",
    headsPerMeter: "પ્રતિ મીટર હેડ",
    headsPerMeterHint: "{mm}mm ફ્રેમ → 1 મીટરમાં {hpm} હેડ",
    stitchMeterCost: "સ્ટીચ કામ (મીટર પ્રમાણે)",
    meterFormulaTitle: "મીટર પ્રમાણે ગણતરી",
    pricePerMeter: "પ્રતિ મીટર દર (1 હેડ કૉલમ)",
    machineRate: "મશીન કલાક દર (₹)",
    laborRate: "મજૂરી કલાક દર (₹)",
    margin: "માર્જિન %",
    threadPrice: "થ્રેડ (₹/g)",
    zariPrice: "ઝરી (₹/g)",
    sequinPrice: "સીક્વિન (₹/pc)",
    saveSettings: "સેટિંગ્સ સાચવો",
    saved: "સાચવ્યું",
    clientQuote: "ક્લાયન્ટ ક્વોટ",
    priceLabel: "ભાવ",
    deliveryBy: "ડિલિવરી સુધી",
    from: "થી",
    back: "પાછળ",
    machineArea: "મશીન એરિયા (હેડ × ફ્રેમ)",
    defaultMachineArea: "ડિફોલ્ટ મશીન એરિયા",
    defaultMachineAreaHint: "નવી ડિઝાઇન બનાવતી વખતે આ આગળથી પસંદ થશે",
    machineAreaHint: "{n} ટુકડા એકસાથે ચાલે છે — મશીન અને મજૂરી ખર્ચ {n} વડે ભાગ્યો",
    machineAreaResultNote: "મશીન અને મજૂરી ખર્ચ {n} ટુકડામાં વહેંચાયો",
    heads: "હેડ",
    blouse: "બ્લાઉઝ",
    kurti: "કુર્તી",
    sareeBorder: "સાડી બોર્ડર",
    lehenga: "લેહેંગા",
    dupatta: "દુપટ્ટો",
    salwar: "સલવાર",
    anarkali: "અનારકલી",
    gown: "ગાઉન",
    odhni: "ઓઢણી / ચૂંદડી",
    ghagra: "ઘાઘરો / સ્કર્ટ",
    jacket: "જેકેટ / શ્રગ",
    waistcoat: "વેસ્ટકોટ / કોટી",
    sareePallu: "સાડી પલ્લુ",
    kurta: "કુર્તા (પુરુષ)",
    embellishments: "ભરત સામગ્રી (બિટ્સ)",
    embellishmentsHint: "દરેક સામગ્રી ચાલુ કરો; પ્રતિ પીસ અંદાજિત જથ્થો અને વજન દાખલ કરો",
    zari: "ઝરી કામ",
    sequin: "સીક્વિન / ટીકી",
    moti: "મોતી (બીડ્સ)",
    mirror: "શીશો / અભ્રક / અબ્લા",
    kundan: "કુંદન સ્ટોન",
    stone: "રાઇનસ્ટોન / ક્રિસ્ટલ",
    khatli: "ખટ્ટી (ધાતુ ફોઇલ)",
    gota: "ગોટા પટ્ટી",
    pipe_bead: "પાઇપ / બુગ્લ બીડ્સ",
    nakh: "નખ (નેઇલ બિટ્સ)",
    approxCount: "અંદાજિત જથ્થો (pcs)",
    approxGrams: "અંદાજિત વજન (g)",
    aiBitsDetected: "AI દ્વારા શોધાયેલ સામગ્રી",
    bitsBreakdown: "બિટ્સ / સામગ્રી વિગતો",
    yes: "હા",
    no: "ના",
  },
};

const I18nCtx = createContext(null);

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(localStorage.getItem("sq_lang") || "en");
  useEffect(() => { localStorage.setItem("sq_lang", lang); }, [lang]);
  const t = (key) => (DICT[lang] && DICT[lang][key]) || DICT.en[key] || key;
  return (
    <I18nCtx.Provider value={{ lang, setLang: setLangState, t, langs: ["en", "hi", "gu"] }}>
      {children}
    </I18nCtx.Provider>
  );
}

export const useI18n = () => useContext(I18nCtx);
